import {
    useRef,
    useCallback,
    useEffect,
    useState,
    useMemo,
    createContext,
    useContext,
    forwardRef,
} from "react";

import {
    XCircleIcon,
    ChevronLeftIcon,
    ChevronDoubleLeftIcon,
    ChevronRightIcon,
} from "@heroicons/react/20/solid";

import {
    formatDateTime,
    classNames,
    genPaginationSequence,
} from "../Utils/helpers";
import { useNFTsDBTable } from "../Utils/hooks";
import { useReords } from "../Utils/hooks";
import { useAuth } from "../Contexts/useAuth";
import { Spinner } from "./Spinner";
import { Refresher } from "./Refresher";
import { Copyable } from "./Copyable";

import solscan_icon from "../assets/Solscan Icon.svg";

const NUM_PER_PAGE = 5;
const NFTS_IMAGE_URL_PREFIX = process.env.REACT_APP_NFTS_IMAGE_URL_PREFIX;
const OPENSEA_URL_PREFIX = process.env.REACT_APP_OPENSEA_URL_PREFIX;
const SOLSCAN_URL_PREFIX = process.env.REACT_APP_SOLSCAN_URL_PREFIX;
const PAGINATION_BREAKPOINT = 768;
const LARGE_SCREEN_PAGINATION_NORMAL_BUTTONS = 7;
const SMALL_SCREEN_PAGINATION_NORMAL_BUTTONS = 5;

const HistoryPageContext = createContext({
    windowSize: 0,
    page: 1,
    total: 0,
    records: [],
    dbErr: null,
    apiErr: null,
    loadingDB: false,
    retrieving: false,
    refreshing: false,
    doRefresh: () => {},
    setPage: (page) => {},
});

export function HistoryPage({ openNewSwappingPage }) {
    const titleRef = useRef(null);
    const [windowSize, setWindowSize] = useState(document.body.offsetWidth);
    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(1);
    const { jwt, solanaPubKey } = useAuth();
    const { dbTable, error: dbErr, isLoading: loadingDB } = useNFTsDBTable();
    const {
        rawRecords,
        total,
        error: apiErr,
        isLoading: retrieving,
        mutate,
        isValidating: refreshing,
    } = useReords({ solanaPubKey, jwt });

    const doRefresh = useCallback(() => {
        setRecords([]);
        mutate(["records", jwt]);
        setPage(1);
    }, [jwt, mutate]);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize(document.body.clientWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (loadingDB || retrieving || refreshing) return;
        let ignore = false;
        (async () => {
            if (ignore) return;
            const keys = rawRecords.map((r) => r["NFTTokenAddr"]);
            const nftsInfo = {};
            try {
                const result = [];
                await dbTable
                    .where("token_address")
                    .anyOf(keys)
                    .each((r) => {
                        nftsInfo[r["token_address"]] = r;
                    });
                rawRecords.forEach((r) => {
                    const { NFTTokenAddr, CreatedAt, ...rest } = r;
                    result.push({
                        NFTTokenAddr,
                        NFTName: nftsInfo[NFTTokenAddr]["token_name"],
                        NFTCollection: nftsInfo[NFTTokenAddr]["type"],
                        ...rest,
                        CreatedAt: formatDateTime(CreatedAt),
                    });
                });
                setRecords(result);
                if (titleRef.current) {
                    titleRef.current.scrollIntoView(true);
                }
            } catch (error) {
                console.log(error);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [rawRecords, dbTable, loadingDB, retrieving, refreshing]);

    return (
        <main className="w-full grow">
            <div className="flex h-full flex-col justify-start px-1 pb-6 md:px-6">
                <PageTitle
                    ref={titleRef}
                    openNewSwappingPage={openNewSwappingPage}
                />
                <div className="flex grow items-start">
                    <div className="mt-12 w-full rounded-lg border border-gray-100 bg-white shadow xl:mt-14 2xl:mt-24">
                        <HistoryPageContext.Provider
                            value={{
                                windowSize,
                                page,
                                total,
                                records,
                                dbErr,
                                apiErr,
                                loadingDB,
                                retrieving,
                                refreshing,
                                setPage,
                                doRefresh,
                            }}
                        >
                            <RecordsTable />
                        </HistoryPageContext.Provider>
                    </div>
                </div>
            </div>
        </main>
    );
}

const PageTitle = forwardRef(function PageTitle(props, ref) {
    return (
        <div
            ref={ref}
            className="flex flex-wrap items-start justify-between gap-6 px-5 pt-12 md:px-0 md:pt-16"
        >
            <div className="flex flex-col items-start gap-3">
                <span className="font-burbank text-2xl font-semibold text-gray-800 sm:text-4xl">
                    Swap Neocollectible to Polygon
                </span>
                <span className="text-xs font-medium text-gray-500 sm:text-base">
                    Swap your Neocollectible on Solana to Polygon today!
                </span>
            </div>
            <button
                onClick={(e) => {
                    e.preventDefault();
                    props.openNewSwappingPage();
                }}
                className="w-fit rounded-full border border-transparent bg-orange-600 px-6 py-2.5 font-burbank text-xl font-semibold text-white shadow-inner hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
                New Swap
            </button>
        </div>
    );
});

function RecordsTable() {
    const {
        page,
        records,
        loadingDB,
        retrieving,
        dbErr,
        apiErr,
        doRefresh,
        refreshing,
    } = useContext(HistoryPageContext);
    const start = (page - 1) * NUM_PER_PAGE;
    const end = start + NUM_PER_PAGE;

    const handleRefresh = useCallback(
        (e) => {
            e.preventDefault();
            doRefresh();
        },
        [doRefresh]
    );
    return (
        <div className="flex flex-col md:min-h-132">
            <div className="flex w-full flex-wrap items-center justify-between border-b border-gray-100 px-3 py-3 sm:flex-nowrap md:px-6">
                <h3 className="text-base font-bold text-gray-800 md:text-lg">
                    Swap History
                </h3>
                <div className="flex-shrink-0">
                    <button
                        disabled={refreshing}
                        onClick={handleRefresh}
                        type="button"
                        className="relative inline-flex items-center rounded-lg px-2 text-base font-medium text-gray-800 transition duration-200 hover:text-gray-900 hover:drop-shadow-lg"
                    >
                        <Refresher size="h-3 w-3" color="text-gray-800" />
                        Refresh
                    </button>
                </div>
            </div>
            <table className="w-full divide-y divide-gray-100">
                <thead>
                    <tr className="bg-gray-50">
                        <th
                            scope="col"
                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-400 md:pl-6"
                        >
                            Item
                        </th>
                        <th
                            scope="col"
                            className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-400 lg:table-cell"
                        >
                            Transaction
                        </th>
                        <th
                            scope="col"
                            className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-400 sm:table-cell"
                        >
                            To
                        </th>
                        <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-400"
                        >
                            Status
                        </th>
                        <th
                            scope="col"
                            className="relative py-3.5 pl-3 pr-4 md:pr-6"
                        >
                            <span className="sr-only">View Neocollectible</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {records.slice(start, end).map((record) => (
                        <tr key={record.ItemLabel}>
                            <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-6">
                                <div className="flex items-center">
                                    <div className="hidden h-12 w-12 flex-shrink-0 lg:block">
                                        <picture>
                                            <source
                                                type="image/avif"
                                                srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.avif`}
                                                width="48"
                                                height="48"
                                            />
                                            <source
                                                type="image/webp"
                                                srcSet={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.webp`}
                                                width="48"
                                                height="48"
                                            />
                                            <img
                                                className="h-12 w-12 rounded"
                                                draggable="false"
                                                loading="lazy"
                                                src={`${NFTS_IMAGE_URL_PREFIX}100x100/${record.NFTTokenAddr}.png`}
                                                alt={record.NFTName}
                                                width="48"
                                                height="48"
                                            />
                                        </picture>
                                    </div>
                                    <div className="ml-0 lg:ml-4">
                                        <div className="font-medium text-gray-800">
                                            {record.NFTName}
                                        </div>
                                        <div className="font-normal text-gray-500">
                                            {record.CreatedAt}
                                        </div>
                                    </div>
                                </div>
                                <dl className="font-normal lg:hidden">
                                    <dt className="sr-only">Transaction</dt>
                                    <dd className="mt-1 truncate text-gray-700">
                                        <div className="inline-flex items-center justify-start">
                                            <Copyable
                                                value={record.SolanaTxSig}
                                            >
                                                {record.SolanaTxSig.slice(
                                                    0,
                                                    4
                                                ) +
                                                    "..." +
                                                    record.SolanaTxSig.slice(
                                                        -4
                                                    )}
                                            </Copyable>
                                            <a
                                                className="ml-0.5"
                                                target="_blank"
                                                href={`${SOLSCAN_URL_PREFIX}${record.SolanaTxSig}/`}
                                                rel="noreferrer noopener"
                                            >
                                                <img
                                                    src={solscan_icon}
                                                    alt="Solscan"
                                                    className="h-4 w-4"
                                                />
                                            </a>
                                        </div>
                                    </dd>
                                    <dt className="sr-only sm:hidden">To</dt>
                                    <dd className="mt-1 truncate text-gray-500 sm:hidden">
                                        <Copyable
                                            value={record.PolygonAcctPubKey}
                                        >
                                            {record.PolygonAcctPubKey.slice(
                                                0,
                                                5
                                            ) +
                                                "..." +
                                                record.PolygonAcctPubKey.slice(
                                                    -4
                                                )}
                                        </Copyable>
                                    </dd>
                                </dl>
                            </td>
                            <td className="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
                                <div className="inline-flex items-center justify-start">
                                    <Copyable value={record.SolanaTxSig}>
                                        {record.SolanaTxSig.slice(0, 4) +
                                            "..." +
                                            record.SolanaTxSig.slice(-4)}
                                    </Copyable>
                                    <a
                                        className="ml-0.5"
                                        target="_blank"
                                        href={`${SOLSCAN_URL_PREFIX}${record.SolanaTxSig}/`}
                                        rel="noreferrer noopener"
                                    >
                                        <img
                                            src={solscan_icon}
                                            alt="Solscan"
                                            className="h-4 w-4"
                                        />
                                    </a>
                                </div>
                            </td>
                            <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                                <Copyable value={record.PolygonAcctPubKey}>
                                    {record.PolygonAcctPubKey.slice(0, 5) +
                                        "..." +
                                        record.PolygonAcctPubKey.slice(-4)}
                                </Copyable>
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                                <StatusLabel status={record.Status} />
                            </td>
                            <td className="py-4 pl-3 pr-4 text-center text-sm font-bold sm:pr-6">
                                {record.Status === "finalized" ? (
                                    <a
                                        target="_blank"
                                        href={`${OPENSEA_URL_PREFIX}${record.PolygonContractAddr}/${record.PolygonTokenId}/`}
                                        rel="noreferrer noopener"
                                        className="text-orange-600 hover:text-orange-800"
                                    >
                                        View Neocollectible
                                    </a>
                                ) : (
                                    <span className="cursor-not-allowed text-gray-300">
                                        View Neocollectible
                                    </span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {records.length === 0 && <LoadingOrError />}
            {!loadingDB && !retrieving && !refreshing && !dbErr && !apiErr && (
                <>
                    {records.length === 0 ? (
                        <div className="flex h-40 flex-col items-center justify-center text-base text-gray-500 md:h-96">
                            <span>You haven’t made any swaps yet.</span>
                            <span>Click “New Swap” to get started.</span>
                        </div>
                    ) : (
                        <PaginationWrapper />
                    )}
                </>
            )}
        </div>
    );
}

function StatusLabel({ status }) {
    const processingStatus = ["sent", "confirmed", "processing"];

    switch (status) {
        case "finalized":
            return (
                <span className="inline-flex items-center rounded-full bg-teal-100 px-3 py-1 text-xs font-bold text-teal-800">
                    Success
                </span>
            );
        case "failed":
            return (
                <span className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-600">
                    Failed
                </span>
            );
        default:
            if (processingStatus.indexOf(status) !== -1) {
                return (
                    <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-600">
                        Processing
                    </span>
                );
            } else {
                return <></>;
            }
    }
}

function PaginationItem({
    type,
    pageNum = null,
    isActive = false,
    isClickable = true,
}) {
    switch (type) {
        case "prev":
            return (
                <button
                    disabled={!isClickable}
                    data-page={pageNum}
                    data-active={isActive}
                    className="relative inline-flex items-center rounded-l border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-800 hover:bg-gray-50 focus:z-20 disabled:cursor-not-allowed disabled:bg-white disabled:text-gray-300"
                >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            );
        case "first":
            return (
                <button
                    disabled={!isClickable}
                    data-page={pageNum}
                    data-active={isActive}
                    className="relative inline-flex items-center rounded-l border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-800 hover:bg-gray-50 focus:z-20 disabled:cursor-not-allowed disabled:bg-white disabled:text-gray-300"
                >
                    <span className="sr-only">First</span>
                    <ChevronDoubleLeftIcon
                        className="h-5 w-5"
                        aria-hidden="true"
                    />
                </button>
            );
        case "next":
            return (
                <button
                    disabled={!isClickable}
                    data-page={pageNum}
                    data-active={isActive}
                    className="relative inline-flex items-center rounded-r border border-gray-200 bg-white px-2 py-2 text-sm font-normal text-gray-800 hover:bg-gray-50 focus:z-20 disabled:cursor-not-allowed disabled:bg-white disabled:text-gray-300"
                >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            );
        case "number":
            return (
                <button
                    disabled={!isClickable}
                    data-page={pageNum}
                    data-active={isActive}
                    className={classNames(
                        isActive
                            ? "z-10 border-orange-600 bg-orange-50 text-orange-600 focus:z-20"
                            : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
                        "relative inline-flex items-center border px-4 py-2 text-sm font-normal focus:z-20"
                    )}
                >
                    {pageNum}
                </button>
            );
        case "delimiter":
            return (
                <span className="relative inline-flex items-center border border-gray-200 bg-white px-4 py-2 text-sm font-normal text-gray-800">
                    ...
                </span>
            );
        default:
            return <></>;
    }
}

function PaginationWrapper() {
    const { windowSize, page, total, setPage } = useContext(HistoryPageContext);
    const numOfPages = Math.ceil(total / NUM_PER_PAGE);
    const from = (page - 1) * NUM_PER_PAGE + 1;
    const to = Math.min(page * NUM_PER_PAGE, total);

    const handleClick = useCallback(
        (e) => {
            e.preventDefault();
            const btn = e.target.closest("button");
            if (btn === null) {
                return;
            }
            const data = btn.dataset;
            if (data.active === "true" || +data.page === 0) {
                return;
            }
            const pageNum = parseInt(data.page);
            setPage(pageNum);
        },
        [setPage]
    );

    // Gen the button sequence based on window size
    const buttonSequence = useMemo(() => {
        if (windowSize >= PAGINATION_BREAKPOINT) {
            return genPaginationSequence(
                page,
                numOfPages,
                LARGE_SCREEN_PAGINATION_NORMAL_BUTTONS
            );
        } else {
            return genPaginationSequence(
                page,
                numOfPages,
                SMALL_SCREEN_PAGINATION_NORMAL_BUTTONS
            );
        }
    }, [windowSize, page, numOfPages]);

    return (
        <div className="grid grow grid-rows-1 place-items-end">
            <div className="flex w-full items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-1 items-center justify-center sm:justify-between">
                    <div className="hidden sm:block">
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-bold">{from}</span> to{" "}
                            <span className="font-bold">{to}</span> of{" "}
                            <span className="font-bold">{total}</span> results
                        </p>
                    </div>
                    <div>
                        <nav
                            onClick={handleClick}
                            className="isolate inline-flex rounded-md shadow-sm"
                            aria-label="Pagination"
                        >
                            {buttonSequence.map((r) => {
                                return (
                                    <PaginationItem
                                        key={r.type + r.pageNum}
                                        type={r.type}
                                        pageNum={r.pageNum}
                                        isActive={r.isActive}
                                        isClickable={r.isClickable}
                                    />
                                );
                            })}
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );
}

function LoadingOrError() {
    const { dbErr, apiErr, loadingDB, retrieving, refreshing } =
        useContext(HistoryPageContext);
    if (dbErr) {
        console.error(dbErr);
        return (
            <div className="flex h-40 items-center justify-center sm:gap-3 md:h-96">
                <XCircleIcon
                    className="h-8 w-8 shrink-0 text-red-400"
                    aria-hidden="true"
                />
                <span className="grow text-center text-sm leading-5 text-orange-600 sm:grow-0">
                    Failed to initialize Neocollectibles information database.
                    Please{" "}
                    <a
                        className="font-bold text-orange-600 underline"
                        href="#error"
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.reload();
                        }}
                    >
                        refresh page
                    </a>
                    .
                </span>
            </div>
        );
    }
    if (apiErr) {
        console.error(apiErr);
        return (
            <div className="flex h-40 items-center justify-center sm:gap-3 md:h-96">
                <XCircleIcon
                    className="h-8 w-8 shrink-0 text-orange-600"
                    aria-hidden="true"
                />
                <span className="grow text-center text-sm leading-5 text-orange-600 sm:grow-0">
                    Failed to retrieving your records. Please{" "}
                    <a
                        className="font-bold text-orange-600 underline"
                        href="#error"
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.reload();
                        }}
                    >
                        refresh page
                    </a>
                    .
                </span>
            </div>
        );
    }
    if (loadingDB) {
        return (
            <div className="flex h-40 items-center justify-center md:h-96">
                <Spinner color="text-orange-500" size="h-5 w-5" />
                <span className="text-sm text-gray-400">
                    Initializing Neocollectibles information database...
                </span>
            </div>
        );
    } else if (retrieving || refreshing) {
        return (
            <div className="flex h-40 items-center justify-center md:h-96">
                <Spinner color="text-orange-500" size="h-5 w-5" />
                <span className="text-sm text-gray-400">
                    Retrieving your records...
                </span>
            </div>
        );
    }

    return <></>;
}
